import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { BehaviorSubject } from "rxjs";

/**
 * Strategies for keeping the screen awake
 */
export enum WakeLockStrategy {
  NONE = "none",
  WAKE_LOCK_API = "wakeLockAPI",
  VIDEO_ELEMENT = "videoElement",
  DOM_INTERACTION = "domInteraction",
}

/**
 * Comprehensive service to prevent screen dimming/timeout
 * Handles browser tab switching and minimizing with multiple fallback strategies
 */
@Injectable({
  providedIn: "root",
})
export class WakeLockService implements OnDestroy {
  // Features detection
  private readonly wakeLockSupported = "wakeLock" in navigator;

  // Wake lock resources
  private wakeLock: WakeLockSentinel | null = null;
  private fallbackVideo: HTMLVideoElement | null = null;
  private heartbeatInterval: number | null = null;
  private domUpdateInterval: number | null = null;

  // State tracking
  private _isActive = new BehaviorSubject<boolean>(false);
  public isActive$ = this._isActive.asObservable();

  private _activeStrategy = new BehaviorSubject<WakeLockStrategy>(
    WakeLockStrategy.NONE,
  );
  public activeStrategy$ = this._activeStrategy.asObservable();

  // Track if we should maintain wake lock
  private shouldKeepAwake = false;
  private visibilityChangeHandler: any = null;
  private focusHandler: any = null;
  private maxRetries = 3;
  private currentRetry = 0;

  constructor(private ngZone: NgZone) {
    this.setupEventListeners();
  }

  /**
   * Set up visibility and focus event listeners
   */
  private setupEventListeners(): void {
    // Handler for when visibility changes (tab switching)
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === "visible") {
        if (this.shouldKeepAwake && !this.isWakeLockActive()) {
          console.log("Page became visible, reacquiring wake lock");
          this.ngZone.run(() => {
            setTimeout(() => void this.activateWakeLock(), 300);
          });
        }
      } else {
        console.log("Page hidden, wake lock will be released by browser");
      }
    };

    // Handler for window focus (helps with some Chrome scenarios)
    this.focusHandler = () => {
      if (this.shouldKeepAwake && !this.isWakeLockActive()) {
        console.log("Window regained focus, reacquiring wake lock");
        this.ngZone.run(() => {
          setTimeout(() => void this.activateWakeLock(), 300);
        });
      }
    };

    // Add the event listeners
    document.addEventListener("visibilitychange", this.visibilityChangeHandler);
    window.addEventListener("focus", this.focusHandler);
  }

  /**
   * Enable wake lock for the application
   * @returns Promise resolving to success status
   */
  public async enableWakeLock(): Promise<boolean> {
    this.shouldKeepAwake = true;
    return this.activateWakeLock();
  }

  /**
   * Disable wake lock for the application
   */
  public disableWakeLock(): void {
    this.shouldKeepAwake = false;
    this.releaseAllWakeLocks();
    this._isActive.next(false);
    this._activeStrategy.next(WakeLockStrategy.NONE);
  }

  /**
   * Check if wake lock is currently active
   */
  public isWakeLockActive(): boolean {
    return this._isActive.value;
  }

  /**
   * Get the current wake lock strategy as a string
   */
  public getActiveStrategy(): string {
    return this._activeStrategy.value;
  }

  /**
   * Try to activate wake lock using all available methods in sequence
   * Will retry up to maxRetries times with exponential backoff
   * @returns Promise resolving to success status
   */
  private async activateWakeLock(): Promise<boolean> {
    // Don't try to activate if the page isn't visible
    if (document.visibilityState !== "visible") {
      console.log("Page not visible, deferring wake lock activation");
      return false;
    }

    // Already active? Don't duplicate
    if (this._isActive.value) {
      return true;
    }

    // Try each strategy in order
    if (await this.tryWakeLockAPI()) {
      this.updateState(true, WakeLockStrategy.WAKE_LOCK_API);
      this.currentRetry = 0;
      return true;
    }

    // if (await this.tryVideoMethod()) {
    //   this.updateState(true, WakeLockStrategy.VIDEO_ELEMENT);
    //   this.currentRetry = 0;
    //   return true;
    // }

    // if (this.tryDomInteractionMethod()) {
    //   this.updateState(true, WakeLockStrategy.DOM_INTERACTION);
    //   this.currentRetry = 0;
    //   return true;
    // }

    // All methods failed - retry with backoff if needed
    if (this.shouldKeepAwake && this.currentRetry < this.maxRetries) {
      this.currentRetry++;
      const delay = Math.min(1000 * Math.pow(2, this.currentRetry), 10000);
      console.log(
        `All wake lock methods failed. Retrying in ${delay}ms (attempt ${this.currentRetry}/${this.maxRetries})`,
      );

      setTimeout(() => {
        void this.activateWakeLock();
      }, delay);
    } else if (this.currentRetry >= this.maxRetries) {
      console.error(
        `Failed to acquire wake lock after ${this.maxRetries} attempts`,
      );
      this.currentRetry = 0;
    }

    return false;
  }

  /**
   * Try to activate the Wake Lock API (modern browsers)
   * @returns Promise<boolean> indicating success
   */
  private async tryWakeLockAPI(): Promise<boolean> {
    if (!this.wakeLockSupported) {
      return false;
    }

    try {
      // Release any existing wake lock first
      if (this.wakeLock) {
        await this.wakeLock.release().catch(() => {});
        this.wakeLock = null;
      }

      // Request new wake lock
      this.wakeLock = await navigator.wakeLock.request("screen");

      // Handle wake lock release event
      this.wakeLock.addEventListener("release", () => {
        console.log("Wake Lock API was released");

        // Only update state if this is still our active strategy
        if (this._activeStrategy.value === WakeLockStrategy.WAKE_LOCK_API) {
          this._isActive.next(false);
          this._activeStrategy.next(WakeLockStrategy.NONE);

          // Try to reacquire if page is visible and we should keep awake
          if (document.visibilityState === "visible" && this.shouldKeepAwake) {
            setTimeout(() => void this.activateWakeLock(), 500);
          }
        }
      });

      console.log("Wake Lock API activated successfully");
      return true;
    } catch (err) {
      console.warn(`Failed to acquire Wake Lock API: ${err}`);
      return false;
    }
  }

  /**
   * Try to activate wake lock using a hidden video element
   * Based on how YouTube and other video sites prevent screen dimming
   * @returns Promise<boolean> indicating success
   */
  private async tryVideoMethod(): Promise<boolean> {
    try {
      // Clean up any existing video resources
      this.cleanupVideoResources();

      // Create a new hidden video element
      this.fallbackVideo = document.createElement("video");
      this.fallbackVideo.setAttribute("playsinline", "");
      this.fallbackVideo.setAttribute("muted", "");
      this.fallbackVideo.setAttribute("loop", "");
      this.fallbackVideo.muted = true;
      this.fallbackVideo.playsInline = true;
      this.fallbackVideo.loop = true;

      // Style it to be invisible
      this.fallbackVideo.style.position = "absolute";
      this.fallbackVideo.style.top = "0";
      this.fallbackVideo.style.left = "0";
      this.fallbackVideo.style.width = "1px";
      this.fallbackVideo.style.height = "1px";
      this.fallbackVideo.style.opacity = "0.01";
      this.fallbackVideo.style.pointerEvents = "none";

      // Create the video source using a base64-encoded minimal MP4
      const source = document.createElement("source");
      // This base64 string is a minimal 1-second silent MP4 video
      source.src =
        "data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAxVtZGF0AAACrQYF//+p3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE1MiByMjg1NCBlOWE1OTAzIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxNyAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTEgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTEgc2NlbmVjdXQ9NDAgaW50cmFfcmVmcmVzaD0wIHJjX2xvb2thaGVhZD00MCByYz1jcmYgbWJ0cmVlPTEgY3JmPTI4LjAgcWNvbXA9MC42MCBxcG1pbj0wIHFwbWF4PTY5IHFwc3RlcD00IGlwX3JhdGlvPTEuNDAgYXE9MToxLjAwAIAAAAAwZYiEAD//8m+P5OXfBeLGOfKE3xQN9vFZaEp9zx+hAAA6SZYvh0OvNYAJXbLu8osz1cySkmH1SMiuUyJ95GItr3HS1iKrQpk5yZUbZ0jA6jjFUdTM3HCQdRMW+4SsRWIKJaqWI7HpTwfmHTmxvRf3qIMIXnF0ADmX8UvAAA6mnvMnzkSdf2NlgAAPcBrjajesBHgD2HKgEq8Ufz+BoQXWQpkCbBWsA+sIqIBQIyQBQKeCQDUCAAGvAABpAQnYCLLQD8qAAAKQwAAAGoEBwCgCaBmAEYKAIQADjwATgAhHwDcFJADygBBQAGfALv/j/2vzxf48f/f/nUf/n//t///e/7v+9Xmt/u////n0/1X+ef17/xX7H/B35AG";
      source.type = "video/mp4";
      this.fallbackVideo.appendChild(source);

      // Add to DOM and play
      document.body.appendChild(this.fallbackVideo);

      // Try to play the video
      await this.fallbackVideo.play();

      // Set up heartbeat to ensure video keeps playing
      this.setupVideoHeartbeat();

      console.log("Video wake lock method activated");
      return true;
    } catch (err) {
      console.warn(`Failed to use video wake lock: ${err}`);
      this.cleanupVideoResources();
      return false;
    }
  }

  /**
   * Set up a heartbeat to ensure video keeps playing
   */
  private setupVideoHeartbeat(): void {
    // Clear any existing interval
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Check video is playing every 10 seconds
    this.heartbeatInterval = setInterval(() => {
      if (
        this.fallbackVideo &&
        (this.fallbackVideo.paused || this.fallbackVideo.ended)
      ) {
        console.log("Video heartbeat detected stopped playback, restarting");
        this.fallbackVideo.play().catch((err) => {
          console.warn("Failed to restart video during heartbeat", err);
          // If we can't restart, try to recreate the video
          void this.tryVideoMethod();
        });
      }
    }, 10000);
  }

  /**
   * Try to activate wake lock using periodic DOM interactions
   * @returns boolean indicating success
   */
  private tryDomInteractionMethod(): boolean {
    try {
      // Clean up any existing interval
      if (this.domUpdateInterval) {
        clearInterval(this.domUpdateInterval);
      }

      // Create a periodic DOM update
      this.domUpdateInterval = setInterval(() => {
        // Create a temporary element that forces a paint
        const tempElement = document.createElement("div");
        tempElement.style.position = "absolute";
        tempElement.style.width = "1px";
        tempElement.style.height = "1px";
        tempElement.style.opacity = "0.01";
        document.body.appendChild(tempElement);

        // Force layout recalculation
        void tempElement.offsetHeight;

        // Remove the element
        setTimeout(() => {
          if (document.body.contains(tempElement)) {
            document.body.removeChild(tempElement);
          }
        }, 100);
      }, 20000); // Every 20 seconds

      console.log("DOM interaction wake lock method activated");
      return true;
    } catch (err) {
      console.warn(`Failed to use DOM interaction method: ${err}`);
      if (this.domUpdateInterval) {
        clearInterval(this.domUpdateInterval);
        this.domUpdateInterval = null;
      }
      return false;
    }
  }

  /**
   * Clean up video-related resources
   */
  private cleanupVideoResources(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.fallbackVideo) {
      try {
        this.fallbackVideo.pause();
        if (document.body.contains(this.fallbackVideo)) {
          document.body.removeChild(this.fallbackVideo);
        }
      } catch (e) {
        // Ignore errors during cleanup
      } finally {
        this.fallbackVideo = null;
      }
    }
  }

  /**
   * Release all wake lock methods
   */
  private releaseAllWakeLocks(): void {
    // Release Wake Lock API
    if (this.wakeLock) {
      this.wakeLock.release().catch(() => {});
      this.wakeLock = null;
    }

    // Clean up video resources
    this.cleanupVideoResources();

    // Clear DOM interaction interval
    if (this.domUpdateInterval) {
      clearInterval(this.domUpdateInterval);
      this.domUpdateInterval = null;
    }
  }

  /**
   * Update the service state
   */
  private updateState(isActive: boolean, strategy: WakeLockStrategy): void {
    this._isActive.next(isActive);
    this._activeStrategy.next(strategy);
    console.log(
      `Wake lock state updated: active=${isActive}, strategy=${strategy}`,
    );
  }

  /**
   * Clean up resources on service destruction
   */
  ngOnDestroy(): void {
    document.removeEventListener(
      "visibilitychange",
      this.visibilityChangeHandler,
    );
    window.removeEventListener("focus", this.focusHandler);
    this.releaseAllWakeLocks();
  }
}
